import * as React from "react";
import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

import { CoachComponents } from "@/interfaces";
import { useAccountSettings } from "@/utils";
import { PermissionGroupTable } from "./PermissionGroupTable";

interface PermissionsSectionProps {
  form: UseFormReturn<any>;
  isDisabled: boolean;
}

export const PermissionsSection: React.FC<PermissionsSectionProps> = ({ form, isDisabled }) => {
  const { hasAccessToCoachComponent } = useAccountSettings();

  const permissions = useMemo(() => {
    return Object.values(CoachComponents).filter(permission => {
      return hasAccessToCoachComponent(permission.id);
    });
  }, [hasAccessToCoachComponent]);

  const groupedPermissions = useMemo(() => {
    const groups: Record<string, { id: string; displayLabel: string }[]> = {
      Library: [],
      Users: [],
      "Other Features": [],
      Settings: [],
    };

    permissions.forEach(p => {
      const category = p.group || "Other Features";
      const displayLabel = p.displayLabel || p.label;

      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push({ id: p.id, displayLabel });
    });

    // Clean up empty groups and sort items with specific priority rules
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      } else {
        groups[key].sort((a, b) => {
          if (key === "Settings") {
            if (a.displayLabel === "General Settings") return -1;
            if (b.displayLabel === "General Settings") return 1;
          }
          if (key === "Users") {
            if (a.displayLabel === "Impersonate Players" && b.displayLabel === "Players") return 1;
            if (a.displayLabel === "Players" && b.displayLabel === "Impersonate Players") return -1;
          }
          return a.displayLabel.localeCompare(b.displayLabel);
        });
      }
    });

    return groups;
  }, [permissions]);

  const changeCategoryPermissions = (items: { id: string }[], level: "NONE" | "READ" | "WRITE") => {
    items.forEach(item => {
      form.setValue(`permissionsMap.${item.id}`, level, { shouldDirty: true, shouldValidate: true });
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1 border-b border-slate-100 pb-3">
        <h4 className="text-xl font-bold text-slate-800">Features & Capabilities</h4>
        <p className="text-sm text-slate-500 leading-relaxed">
          <strong>What can they do?</strong> Control what features and actions (viewing, editing, or none) this
          assistant is allowed to perform.
        </p>
      </div>

      {Object.entries(groupedPermissions).map(([category, items]) => (
        <PermissionGroupTable
          key={category}
          category={category}
          items={items}
          form={form}
          isDisabled={isDisabled}
          changeCategoryPermissions={changeCategoryPermissions}
        />
      ))}
    </div>
  );
};
